import { useState } from 'react';

type Props = {
  defaultValue?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  children: (props: {
    value?: string;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    type?: string;
  }) => JSX.Element;
};

const trimLeadingZeros = (value: string) => (value.length > 1 ? value.replace(/^0+/, '') : value);

const toChunks = <T,>(array: T[], chunkSize: number): T[][] =>
  [...Array(Math.ceil(array.length / chunkSize))].map((_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize)
  );

export const localize = (value: string) =>
  toChunks(trimLeadingZeros(value).split('').reverse(), 3)
    .map((chunk) => chunk.join(''))
    .join(',')
    .split('')
    .reverse()
    .join('');

const unlocalize = (value: string) => value.replace(/,/g, '');

const digitsRegex = /^[0-9]+$/;

/** This component only works with whole numbers */
const LocalizedMask = ({ children, ...props }: Props) => {
  const [localizedValue, setLocalizedValue] = useState(props.defaultValue);

  const isControlled = props.value !== undefined;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === '') {
      setLocalizedValue('');
      props.onChange?.(event);
      return;
    }

    const unlocalizedValue = unlocalize(value);

    if (!digitsRegex.test(unlocalizedValue)) {
      return;
    }

    if (!isControlled) {
      setLocalizedValue(localize(unlocalizedValue));
    }

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: unlocalizedValue,
        name: event.target.name,
      },
    };
    props.onChange?.(newEvent);
  };

  const localizedValueProp = isControlled
    ? !digitsRegex.test(props.value?.toString() ?? '')
      ? props.value
      : localize(props.value?.toString() ?? '')
    : localizedValue;

  return children({ value: localizedValueProp, onChange, type: 'text' });
};

export default LocalizedMask;
